import React, { useCallback, useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'

import { PAGINATION } from '../../constants'
import { showErrorNotification } from '../../helpers'
import { useEventsQuery, useUsersQuery } from '../../hooks'
import { idPropType } from '../../prop-types'

import HistoryDrawer from './history-drawer'

const MetadataHistoryDrawer = ({
  entityId,
  eventTypes,
  eventMetadataTypes,
  onClose,
  renderEvent,
  filterEventTypes,
}) => {
  const {
    page = PAGINATION.DEFAULT_PAGE,
    pageSize = PAGINATION.DEFAULT_PAGE_SIZE,
    createdByIds,
    startAt,
    endAt,
    types,
    subtypes,
    searchTerm,
    exactSearchTerm,
    setQueryParams,
  } = useQueryParams()

  const {
    options: userOptions,
    isLoading: isFetchUsersLoading,
    error: fetchUsersError,
  } = useUsersQuery()

  const typesToFilter = useMemo(
    () => [...(!isEmpty(types) ? types : eventTypes)],
    [eventTypes, types],
  )

  const filters = useMemo(
    () => ({
      types: typesToFilter,
      subtypes,
      entityId,
      entityTypes: eventMetadataTypes,
      createdByIds,
      startAt,
      endAt,
      searchTerm,
      exactSearchTerm,
    }),
    [
      typesToFilter,
      subtypes,
      entityId,
      eventMetadataTypes,
      createdByIds,
      startAt,
      endAt,
      searchTerm,
      exactSearchTerm,
    ],
  )

  const {
    data: { results: events, total },
    isLoading: isFetchEvents,
    error: fetchEventsError,
  } = useEventsQuery(page, pageSize, filters)

  const handleFilter = useCallback(
    data => {
      setQueryParams({ ...data, page: 1 })
    },
    [setQueryParams],
  )

  const handleOnChangePagination = useCallback(
    (newPage, newPageSize) =>
      setQueryParams({
        page: newPageSize !== pageSize ? 1 : newPage,
        pageSize: newPageSize,
      }),
    [pageSize, setQueryParams],
  )

  const handleOnClose = useCallback(() => {
    const filters = {
      page: null,
      pageSize: null,
      createdByIds: null,
      startAt: null,
      endAt: null,
      types: null,
      subtypes: null,
    }

    onClose(filters)
  }, [onClose])

  useEffect(() => {
    if (fetchUsersError || fetchEventsError) {
      showErrorNotification({
        title: 'Metadata Events error',
        description: fetchUsersError || fetchEventsError,
      })
    }
  }, [fetchEventsError, fetchUsersError])

  return (
    <HistoryDrawer
      loading={isFetchEvents || isFetchUsersLoading}
      events={events}
      onClose={handleOnClose}
      userOptions={userOptions}
      createdByIds={createdByIds}
      startAt={startAt}
      endAt={endAt}
      types={types}
      subtypes={subtypes}
      searchTerm={searchTerm}
      exactSearchTerm={exactSearchTerm}
      page={page}
      pageSize={pageSize}
      total={total}
      onChangePagination={handleOnChangePagination}
      onFilter={handleFilter}
      renderEvent={renderEvent}
      filterEventTypes={filterEventTypes}
      eventMetadataTypes={eventMetadataTypes}
    />
  )
}

MetadataHistoryDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  eventMetadataTypes: PropTypes.arrayOf(PropTypes.string),
  eventTypes: PropTypes.arrayOf(PropTypes.string),
  renderEvent: PropTypes.func,
  entityId: idPropType,
  filterEventTypes: PropTypes.bool,
}

MetadataHistoryDrawer.defaultProps = {
  eventMetadataTypes: [],
  entityId: undefined,
  eventTypes: [],
  renderEvent: undefined,
  filterEventTypes: false,
}

export default MetadataHistoryDrawer
